import { QueryFunctionContext } from '@tanstack/react-query'
import { DELETE, GET, POST, PUT } from '../../../redux/middleware/types'
import loanQueryKeys from '../queries/loanQueryKeys'
import { LoanSummaryReport } from '../types/LoanSummary'
import loanEndpoints from '../api/loanEndpoints'
import backendRequest from '../../../services/api/backendRequest'
import { LoanInstalment } from '../types/LoanInstalment'
import { LoanInstalmentCorrection } from '../types/LoanInstallmentCorrection'
import { Loan } from '../types/Loan'
import { BudgetingScenario } from '../../scenario/types/BudgetingScenario'

export const fetchLoanSummary = async ({
  queryKey: [{ companyId, date, isExisting, budgetingScenarioId }],
  signal
}: QueryFunctionContext<ReturnType<(typeof loanQueryKeys)['summary']>>) => {
  return backendRequest<LoanSummaryReport>({
    method: GET,
    url: loanEndpoints.summary(companyId),
    params: { date, isExisting, budgetingScenarioId },
    signal
  })
}

export const fetchLoanExport = async ({
  companyId,
  date,
  isExisting,
  budgetingScenarioId
}: QueryFunctionContext<ReturnType<(typeof loanQueryKeys)['summary']>>['queryKey'][0]) => {
  return backendRequest<Blob>({
    method: GET,
    url: loanEndpoints.export(companyId),
    params: { date, isExisting, budgetingScenarioId },
    responseType: 'blob'
  })
}

export const fetchLoanTemplate = async () => {
  return backendRequest<Blob>({
    method: GET,
    url: loanEndpoints.template(),
    responseType: 'blob'
  })
}

export const createLoanImport = async ({
  data,
  companyId,
  budgetingScenarioId
}: {
  data: FormData
  companyId: string
  budgetingScenarioId?: BudgetingScenario['id']
}) => {
  return backendRequest<FormData>({
    headers: { 'Content-Type': 'multipart/form-data' },
    method: POST,
    url: loanEndpoints.import(companyId),
    params: { budgetingScenarioId },
    data
  })
}

export const fetchLoan = async (companyId: string, loanId: number) => {
  return backendRequest<Loan>({
    method: GET,
    url: loanEndpoints.detail(companyId, loanId)
  })
}

export const updateLoan = async (data: Loan, companyId: string, loanId: number) => {
  return backendRequest<Loan>({
    method: PUT,
    url: loanEndpoints.detail(companyId, loanId),
    data
  })
}

export const deleteLoan = async (companyId: string, loanId: number) => {
  return backendRequest<Loan>({
    method: DELETE,
    url: loanEndpoints.detail(companyId, loanId)
  })
}

export const createLoan = async (
  data: Omit<Loan, 'id'>,
  companyId: string,
  budgetingScenarioId?: BudgetingScenario['id']
) => {
  return backendRequest<Loan>({
    method: POST,
    url: loanEndpoints.list(companyId),
    params: { budgetingScenarioId },
    data
  })
}

export const fetchLoanInstalments = async ({
  queryKey: [{ companyId, loanId, budgetingScenarioId }],
  signal
}: QueryFunctionContext<ReturnType<(typeof loanQueryKeys)['instalments']>>) => {
  return backendRequest<LoanInstalment[]>({
    method: GET,
    url: loanEndpoints.instalments(companyId, loanId),
    params: { budgetingScenarioId },
    signal
  })
}

export const createLoanInstalmentsCorrection = async (
  data: Partial<LoanInstalmentCorrection>,
  companyId: string,
  loanId: number,
  budgetingScenarioId?: BudgetingScenario['id']
) => {
  return backendRequest<LoanInstalmentCorrection>({
    method: POST,
    url: loanEndpoints.corrections(companyId, loanId),
    params: { budgetingScenarioId },
    data
  })
}

export const updateLoanInstalmentsCorrection = async (
  data: Partial<LoanInstalmentCorrection>,
  companyId: string,
  loanId: number,
  correctionId: number,
  budgetingScenarioId?: BudgetingScenario['id']
) => {
  return backendRequest<LoanInstalmentCorrection>({
    method: PUT,
    url: loanEndpoints.correction(companyId, loanId, correctionId),
    params: { budgetingScenarioId },
    data
  })
}

export const deleteLoanInstalmentsCorrection = async (
  companyId: string,
  loanId: number,
  correctionId: number,
  budgetingScenarioId?: BudgetingScenario['id']
) => {
  return backendRequest<LoanInstalmentCorrection>({
    method: DELETE,
    url: loanEndpoints.correction(companyId, loanId, correctionId),
    params: { budgetingScenarioId }
  })
}
